import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Table, Tag, Spin, message, Button, Select } from "antd";
import {
  UserOutlined, CloseOutlined, ShoppingCartOutlined,
  RollbackOutlined, DollarOutlined, CreditCardOutlined, StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { apiClient } from "../../../shared/api/AllApi";
import { formatCurrency } from "../../../utils/formatters";
import { parseLocalizedName } from "../../../utils/localization";
import { readCachedSystemConfig } from "../../../shared/lib/systemConfig";
import { resolveDatePreset } from "../../../shared/lib/dateFilterPresets";
import { createRequestGuard, isAbortError } from "../../../shared/lib/request";
import { orderDocNo } from "../../../shared/lib/documentNumbers";
import { PageHeader, StatCard, T, cardStyle } from "../../../shared/ui/PageShell";
import DateRangeFilter from "../../../shared/ui/DateRangeFilter";
import useTablePagination from "../../../shared/ui/useTablePagination";
import { useTranslation } from "react-i18next";
import "../../../shared/styles/DailyRegister.css";

// Minutes past midnight for the configured business-day cutoff (e.g. "02:00" → 120).
const businessCutoffMinutes = () => {
  const [h, m] = String(readCachedSystemConfig()?.business_day_start || "00:00").split(":");
  return (parseInt(h, 10) || 0) * 60 + (parseInt(m, 10) || 0);
};

const currentBusinessDate = () => dayjs().subtract(businessCutoffMinutes(), "minute");

const readSessionUser = () => {
  try {
    return JSON.parse(localStorage.getItem("sessionUser") || "{}") || {};
  } catch {
    return {};
  }
};

/** Apply HH:mm (or HH:mm:ss) onto a dayjs calendar day. */
const applyClock = (base, clock) => {
  const parts = String(clock || "").trim().split(":");
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10) || 0;
  const s = parseInt(parts[2], 10) || 0;
  if (!Number.isFinite(h)) return null;
  return base.hour(h).minute(m).second(s).millisecond(0);
};

/**
 * Default = full business "today" (00:00 → 23:59).
 * Use `defaultRegisterRange(user, { shift: true })` or the "My shift" button for shift hours.
 * Overnight shifts (e.g. 17:00 → 03:00) span into the next calendar day.
 * If you open the register after midnight but still before shift end, the window
 * starts on the previous day (so 5pm–3am night staff see the correct shift).
 */
export const defaultRegisterRange = (user = readSessionUser(), { shift = false } = {}) => {
  const now = dayjs();
  if (shift) {
    const startClock = user?.shift_start;
    const endClock = user?.shift_end;
    const startToday = applyClock(now.startOf("day"), startClock);
    const endToday = applyClock(now.startOf("day"), endClock);
    if (startToday && endToday) {
      const overnight = !endToday.isAfter(startToday);
      if (overnight) {
        // Still in the overnight tail (after midnight, before shift_end) → shift began yesterday.
        const endThisMorning = endToday;
        if (now.isBefore(endThisMorning) || now.isSame(endThisMorning)) {
          const start = applyClock(now.subtract(1, "day").startOf("day"), startClock);
          return [start, endThisMorning];
        }
        return [startToday, endToday.add(1, "day")];
      }
      return [startToday, endToday];
    }
    if (startToday && !endToday) return [startToday, now.endOf("day")];
  }
  return resolveDatePreset("today", { withTime: true, now: currentBusinessDate() });
};

/** Calendar days covered by a from/to range (inclusive), capped for safety. */
const daysInRange = (range, maxDays = 62) => {
  if (!range?.[0] || !range?.[1]) return [];
  const days = [];
  let d = range[0].startOf("day");
  const last = range[1].startOf("day");
  let n = 0;
  while (!d.isAfter(last) && n < maxDays) {
    days.push(d.format("YYYY-MM-DD"));
    d = d.add(1, "day");
    n += 1;
  }
  return days;
};

const dedupeById = (rows) => {
  const seen = new Set();
  const out = [];
  (rows || []).forEach((row) => {
    const key = row?.id != null ? `id:${row.id}` : `row:${out.length}:${row?.created_at || ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });
  return out;
};

/** Rebuild cashier summary from sales in range + returns that belong to those sales. */
const rebuildByUser = (orders, returns) => {
  const map = {};
  const ensure = (id, name) => {
    const key = id != null ? `u:${id}` : `n:${name || "unknown"}`;
    if (!map[key]) {
      map[key] = {
        user_id: id,
        user_name: name || "—",
        sale_count: 0,
        cash_total: 0,
        credit_total: 0,
        refunds: 0,
        cancelled: 0,
        cancel_count: 0,
        total: 0,
      };
    } else if (name && map[key].user_name === "—") {
      map[key].user_name = name;
    }
    return map[key];
  };

  (orders || []).forEach((o) => {
    const u = ensure(
      o.cashier_id ?? o.user_id ?? o.created_by ?? null,
      o.cashier || o.cashier_name || o.user_name || o.created_by_name
    );
    const amt = parseFloat(o.total_price) || 0;
    if (o.status === "cancelled") {
      u.cancelled += amt;
      u.cancel_count += 1;
      return;
    }
    const refunded = Math.max(
      parseFloat(o.total_returned) || 0,
      parseFloat(o.refunded_amount) || 0
    );
    u.sale_count += 1;
    if (String(o.payment_method || "").toLowerCase() === "cash") u.cash_total += amt;
    else u.credit_total += amt;
    u.total += amt;
    // Prefer order-level refund totals (backend already attaches return activity).
    if (refunded > 0) u.refunds += refunded;
  });

  // Also add return rows linked to these sales (covers APIs that only send returns separately).
  const orderIds = new Set((orders || []).map((o) => String(o.id)).filter(Boolean));
  const alreadyCounted = new Set(
    (orders || [])
      .filter((o) => o.status !== "cancelled" && (parseFloat(o.total_returned) || parseFloat(o.refunded_amount) || 0) > 0)
      .map((o) => String(o.id))
  );

  (returns || []).forEach((r) => {
    const oid = r.order_id != null ? String(r.order_id) : null;
    if (oid && !orderIds.has(oid)) return; // return for a sale outside this register window — skip
    if (oid && alreadyCounted.has(oid)) return; // already counted from order.total_returned
    const u = ensure(
      r.cashier_id ?? r.processed_by ?? r.user_id ?? r.created_by ?? null,
      r.cashier || r.processed_by_name || r.user_name || r.user
    );
    u.refunds += parseFloat(r.refund_amount) || 0;
  });

  return Object.values(map).sort((a, b) => String(a.user_name).localeCompare(String(b.user_name)));
};

const saleInWindow = (order, from, to) => {
  // Register = what was sold in the period. Keep the sale even if it was returned later.
  if (!order?.created_at) return true;
  const d = dayjs(order.created_at);
  if (!d.isValid()) return true;
  const t = d.valueOf();
  return t >= from.valueOf() && t <= to.valueOf();
};

const farmerNameOf = (row, language) => {
  if (!row) return "—";
  if (row.vendor && typeof row.vendor === "object") {
    return parseLocalizedName(row.vendor.name ?? row.vendor, language, row.vendor.name || "—");
  }
  if (row.vendor) return String(row.vendor);
  return "—";
};

const DailyRegister = () => {
  const { t, i18n } = useTranslation();
  const { pagination: ordersPagination, resetPage: resetOrdersPage } = useTablePagination();
  const { pagination: returnsPagination, resetPage: resetReturnsPage } = useTablePagination();
  const { pagination: cancellationsPagination, resetPage: resetCancellationsPage } = useTablePagination();
  const { pagination: productsSoldPagination, resetPage: resetProductsSoldPage } = useTablePagination();
  const resetAllPages = () => {
    resetOrdersPage();
    resetReturnsPage();
    resetCancellationsPage();
    resetProductsSoldPage();
  };
  const [dateRange, setDateRange] = useState(() => defaultRegisterRange());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedFarmerId, setSelectedFarmerId] = useState(null);
  const currency = readCachedSystemConfig()?.currency_code || "IQD";
  const guardRef = useRef(createRequestGuard());

  const sessionUser = useMemo(() => readSessionUser(), []);
  const shiftHint = useMemo(
    () =>
      t(
        "registerRangeHintSales",
        "Default is today. Use “My shift” for your working hours (supports overnight, e.g. 17:00→03:00), or pick from/to times. Filter by cashier or farmer — summary cards update with the filters."
      ),
    [t]
  );

  const load = useCallback(async (range) => {
    if (!range?.[0] || !range?.[1]) return;
    const req = guardRef.current.next();
    setLoading(true);
    setSelectedUser(null);
    setSelectedFarmerId(null);
    try {
      const days = daysInRange(range);
      const from = range[0];
      const to = range[1];
      const results = await Promise.all(
        days.map((date) =>
          apiClient
            .get("/pos/daily-register", {
              params: {
                date,
                date_from: from.format("YYYY-MM-DD HH:mm:ss"),
                date_to: to.format("YYYY-MM-DD HH:mm:ss"),
              },
              signal: req.signal,
            })
            .then((res) => res.data)
            .catch((e) => {
              if (isAbortError(e)) throw e;
              return null;
            })
        )
      );
      if (!req.isCurrent()) return;

      // Sales filtered by sale time (cashier activity in the window).
      const ordersInRange = dedupeById(results.flatMap((r) => r?.orders || []))
        .filter((o) => saleInWindow(o, from, to));

      const orderIds = new Set(ordersInRange.map((o) => String(o.id)).filter(Boolean));

      // Keep returns that belong to sales in this window, OR were processed in this window
      // (covers returns of older sales, and fully returned orders that may lack a parent row).
      const returnsForSales = dedupeById(results.flatMap((r) => r?.returns || []))
        .filter((r) => {
          if (r?.order_id != null && orderIds.has(String(r.order_id))) return true;
          if (r?.created_at) {
            const d = dayjs(r.created_at);
            if (d.isValid()) {
              const t = d.valueOf();
              return t >= from.valueOf() && t <= to.valueOf();
            }
          }
          return r?.order_id == null;
        });

      const byUser = rebuildByUser(ordersInRange, returnsForSales);

      const cash_amount = ordersInRange
        .filter((o) => String(o.payment_method || "").toLowerCase() === "cash")
        .reduce((s, o) => s + (parseFloat(o.total_price) || 0), 0);
      const credit_amount = ordersInRange
        .filter((o) => String(o.payment_method || "").toLowerCase() !== "cash")
        .reduce((s, o) => s + (parseFloat(o.total_price) || 0), 0);
      const total_refunds = byUser.reduce((s, u) => s + (parseFloat(u.refunds) || 0), 0);

      setData({
        orders: ordersInRange,
        returns: returnsForSales,
        by_user: byUser,
        total_sales: ordersInRange.length,
        total_amount: cash_amount + credit_amount,
        cash_amount,
        credit_amount,
        total_refunds,
      });
    } catch (e) {
      if (isAbortError(e)) return;
      if (req.isCurrent()) {
        message.error(t("loadError", "Failed to load"));
        setData(null);
      }
    } finally {
      if (req.isCurrent()) setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    load(dateRange);
    resetAllPages();
    return () => guardRef.current.abort();
  }, [dateRange, load]);

  const matchesCashier = (row, user) => {
    if (!user) return true;
    const uid = String(user.user_id);
    return [row.cashier_id, row.processed_by, row.user_id, row.created_by]
      .filter((v) => v != null)
      .some((v) => String(v) === uid);
  };

  const orderHasFarmer = (order, farmerId) => {
    if (farmerId == null) return true;
    return (order.items || []).some((it) => String(it.vendor_id) === String(farmerId));
  };

  // Data is already narrowed to the selected from/to window in `load`.
  const cashierOrders = selectedUser
    ? (data?.orders || []).filter((o) => matchesCashier(o, selectedUser))
    : (data?.orders || []);

  const cashierReturns = selectedUser
    ? (data?.returns || []).filter((r) => matchesCashier(r, selectedUser))
    : (data?.returns || []);

  const filteredOrders = selectedFarmerId != null
    ? cashierOrders.filter((o) => orderHasFarmer(o, selectedFarmerId))
    : cashierOrders;

  const dayReturns = selectedFarmerId != null
    ? cashierReturns.filter((r) => String(r.vendor_id) === String(selectedFarmerId))
    : cashierReturns;

  const dayCancellations = filteredOrders.filter((o) => o.status === "cancelled");

  const farmerOptions = useMemo(() => {
    const map = new Map();
    (cashierOrders || []).forEach((o) => {
      (o.items || []).forEach((it) => {
        if (it.vendor_id == null) return;
        const label = farmerNameOf(it, i18n.language);
        if (!map.has(String(it.vendor_id))) {
          map.set(String(it.vendor_id), {
            value: it.vendor_id,
            label: label !== "—" ? label : `#${it.vendor_id}`,
          });
        }
      });
    });
    (cashierReturns || []).forEach((r) => {
      if (r.vendor_id == null) return;
      const label = farmerNameOf(r, i18n.language);
      if (!map.has(String(r.vendor_id))) {
        map.set(String(r.vendor_id), {
          value: r.vendor_id,
          label: label !== "—" ? label : `#${r.vendor_id}`,
        });
      }
    });
    return Array.from(map.values()).sort((a, b) => String(a.label).localeCompare(String(b.label)));
  }, [cashierOrders, cashierReturns, i18n.language]);

  const summary = useMemo(() => {
    const activeOrders = filteredOrders.filter((o) => o.status !== "cancelled");
    const cancelledOrders = filteredOrders.filter((o) => o.status === "cancelled");

    const sold = selectedFarmerId != null
      ? activeOrders.reduce((s, o) => (
        s + (o.items || [])
          .filter((it) => String(it.vendor_id) === String(selectedFarmerId))
          .reduce((ls, it) => ls + (parseFloat(it.total_price) || 0), 0)
      ), 0)
      : activeOrders.reduce((s, o) => s + (parseFloat(o.total_price) || 0), 0);

    const cancelled = selectedFarmerId != null
      ? cancelledOrders.reduce((s, o) => (
        s + (o.items || [])
          .filter((it) => String(it.vendor_id) === String(selectedFarmerId))
          .reduce((ls, it) => ls + (parseFloat(it.total_price) || 0), 0)
      ), 0)
      : cancelledOrders.reduce((s, o) => s + (parseFloat(o.total_price) || 0), 0);

    const returnedFromRows = dayReturns.reduce((s, r) => s + (parseFloat(r.refund_amount) || 0), 0);
    // Prefer explicit return rows when present (avoids double-count with order.total_returned).
    // Do not treat cancellation refunds as product returns.
    const returned = returnedFromRows > 0
      ? returnedFromRows
      : activeOrders.reduce((s, o) => s + Math.max(
        parseFloat(o.total_returned) || 0,
        parseFloat(o.refunded_amount) || 0
      ), 0);

    // Payments/debt apply to the whole order, not per line item — when a farmer filter
    // is active, prorate by that farmer's share of the order total so Paid/Debt line up
    // with Sold/Cancelled instead of leaking other farmers' amounts into the total.
    const farmerShareOf = (o) => {
      if (selectedFarmerId == null) return 1;
      const total = parseFloat(o.total_price) || 0;
      if (total <= 0) return 0;
      const farmerTotal = (o.items || [])
        .filter((it) => String(it.vendor_id) === String(selectedFarmerId))
        .reduce((s, it) => s + (parseFloat(it.total_price) || 0), 0);
      return farmerTotal / total;
    };

    const paid = activeOrders.reduce((s, o) => {
      const share = farmerShareOf(o);
      if (share === 0) return s;
      const paidAmt = parseFloat(o.amount_paid);
      if (Number.isFinite(paidAmt)) {
        return s + Math.max(0, paidAmt - (parseFloat(o.refunded_amount) || 0)) * share;
      }
      const total = parseFloat(o.total_price) || 0;
      const outstanding = parseFloat(o.outstanding_balance);
      if (Number.isFinite(outstanding)) return s + Math.max(0, total - outstanding) * share;
      return s + (String(o.payment_method || "").toLowerCase() === "cash" ? total * share : 0);
    }, 0);

    const debt = activeOrders.reduce((s, o) => {
      const share = farmerShareOf(o);
      if (share === 0) return s;
      const outstanding = parseFloat(o.outstanding_balance);
      if (Number.isFinite(outstanding)) return s + Math.max(0, outstanding) * share;
      if (String(o.payment_method || "").toLowerCase() === "cash") return s;
      const total = parseFloat(o.total_price) || 0;
      const paidAmt = parseFloat(o.amount_paid) || 0;
      return s + Math.max(0, total - paidAmt) * share;
    }, 0);

    return {
      sold,
      returned,
      cancelled,
      paid,
      debt,
      saleCount: activeOrders.length,
      cancelCount: cancelledOrders.length,
    };
  }, [filteredOrders, dayReturns, selectedFarmerId]);

  const productsSold = (() => {
    const map = {};
    const bump = (product_id, product, vendor_id, vendor, sign, w, q, amount) => {
      const name = parseLocalizedName(product, i18n.language, `#${product_id}`);
      const farmer = vendor
        ? (typeof vendor === "object"
          ? parseLocalizedName(vendor.name ?? vendor, i18n.language, vendor.name || "—")
          : String(vendor))
        : "—";
      const key = `${product_id ?? name}::${vendor_id ?? farmer}`;
      if (!map[key]) map[key] = { key, name, farmer, vendor_id, qty: 0, weight: 0, amount: 0 };
      if (w > 0) map[key].weight += sign * w;
      if (q > 0) map[key].qty += sign * q;
      map[key].amount += sign * amount;
    };
    filteredOrders
      .filter((o) => o.status !== "cancelled")
      .forEach((o) =>
        (o.items || [])
          .filter((it) => selectedFarmerId == null || String(it.vendor_id) === String(selectedFarmerId))
          .forEach((it) =>
            bump(
              it.product_id,
              it.product,
              it.vendor_id,
              it.vendor,
              1,
              parseFloat(it.weight) || 0,
              parseFloat(it.quantity) || 0,
              parseFloat(it.total_price) || 0
            )
          )
      );
    dayReturns.forEach((r) =>
      bump(
        r.product_id,
        r.product,
        r.vendor_id,
        r.vendor,
        -1,
        r.is_weight ? parseFloat(r.quantity) || 0 : 0,
        r.is_weight ? 0 : parseFloat(r.quantity) || 0,
        parseFloat(r.refund_amount) || 0
      )
    );
    return Object.values(map)
      .filter((r) => r.qty !== 0 || r.weight !== 0 || r.amount !== 0)
      .sort((a, b) => b.amount - a.amount);
  })();

  const orderItemColumns = [
    {
      title: <b>{t("product", "Product")}</b>, dataIndex: "product", key: "product",
      render: (v, r) => <span style={{ color: T.text }}>{parseLocalizedName(v, i18n.language, `#${r.product_id}`)}</span>,
    },
    {
      title: <b>{t("farmer", "Farmer")}</b>, key: "farmer", width: 160,
      render: (_, r) => {
        const name = farmerNameOf(r, i18n.language);
        return <span style={{ color: name === "—" ? T.muted : T.text, fontWeight: 600 }}>{name}</span>;
      },
    },
    {
      title: <b>{t("qtyKg", "Qty / kg")}</b>, key: "qty", width: 160,
      render: (_, r) => {
        const w = parseFloat(r.weight) || 0;
        const q = parseFloat(r.quantity) || 0;
        const parts = [];
        if (q > 0) parts.push(<Tag key="q" color="blue">{q} {t("qty", "qty")}</Tag>);
        if (w > 0) parts.push(<Tag key="w" color="green">{w} {t("kg", "KG")}</Tag>);
        return parts.length ? <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>{parts}</span> : "—";
      },
    },
    {
      title: <b>{t("price", "Price")}</b>, dataIndex: "single_price", key: "single_price", width: 120,
      render: (v) => <span style={{ color: T.sub }}>{formatCurrency(v, currency)}</span>,
    },
    {
      title: <b>{t("subtotal", "Subtotal")}</b>, dataIndex: "total_price", key: "total_price", width: 130,
      render: (v) => <b style={{ color: T.text }}>{formatCurrency(v, currency)}</b>,
    },
  ];

  const productsSoldColumns = [
    { title: "#", key: "idx", width: 50, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    { title: <b>{t("product", "Product")}</b>, dataIndex: "name", key: "name", render: (v) => <span style={{ fontWeight: 600, color: T.text }}>{v}</span> },
    {
      title: <b>{t("farmer", "Farmer")}</b>, dataIndex: "farmer", key: "farmer", width: 160,
      render: (v) => <span style={{ fontWeight: 600, color: !v || v === "—" ? T.muted : T.text }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("qtySold", "Qty sold")}</b>, key: "qtySold", width: 180,
      render: (_, r) => {
        const parts = [];
        if (r.qty) parts.push(<Tag key="q" color="blue">{Math.round(r.qty * 1000) / 1000} {t("qty", "qty")}</Tag>);
        if (r.weight) parts.push(<Tag key="w" color="green">{Math.round(r.weight * 1000) / 1000} {t("kg", "KG")}</Tag>);
        return parts.length ? <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>{parts}</span> : "—";
      },
    },
    {
      title: <b>{t("total", "Total")}</b>, dataIndex: "amount", key: "amount", width: 150,
      render: (v) => <b style={{ color: T.green }}>{formatCurrency(v, currency)}</b>,
    },
  ];

  const userColumns = [
    {
      title: <b>{t("user", "User")}</b>, dataIndex: "user_name", key: "user_name",
      render: (v, record) => (
        <span
          style={{ fontWeight: 600, cursor: "pointer", color: selectedUser?.user_id === record.user_id ? T.primary : T.text }}
          onClick={() => { setSelectedUser(selectedUser?.user_id === record.user_id ? null : record); resetAllPages(); }}
        >
          <UserOutlined style={{ marginInlineEnd: 6, color: T.primary }} />{v}
        </span>
      ),
    },
    {
      title: <b>{t("sales", "Sales")}</b>, dataIndex: "sale_count", key: "sale_count",
      render: (v) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: <b>{t("cash", "Cash")}</b>, dataIndex: "cash_total", key: "cash_total",
      render: (v) => <span style={{ color: T.green, fontWeight: 600 }}>{formatCurrency(v, currency)}</span>,
    },
    {
      title: <b>{t("credit", "Credit")}</b>, dataIndex: "credit_total", key: "credit_total",
      render: (v) => <span style={{ color: v > 0 ? "#d97706" : T.muted, fontWeight: 600 }}>{formatCurrency(v, currency)}</span>,
    },
    {
      title: <b>{t("posOrdersReturned", "Returned")}</b>, dataIndex: "refunds", key: "refunds",
      render: (v) => <span style={{ color: v > 0 ? T.red : T.muted, fontWeight: 600 }}>{v > 0 ? `− ${formatCurrency(v, currency)}` : formatCurrency(0, currency)}</span>,
    },
    {
      title: <b>{t("statusCancelled", "Cancelled")}</b>, dataIndex: "cancelled", key: "cancelled",
      render: (v) => <span style={{ color: v > 0 ? T.red : T.muted, fontWeight: 600 }}>{v > 0 ? `− ${formatCurrency(v, currency)}` : formatCurrency(0, currency)}</span>,
    },
    {
      title: <b>{t("total", "Total")}</b>, dataIndex: "total", key: "total",
      render: (v) => <b style={{ color: T.text }}>{formatCurrency(v, currency)}</b>,
    },
  ];

  const returnColumns = [
    { title: "#", key: "idx", width: 50, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    {
      title: <b>{t("order", "Order")}</b>, key: "document_no", width: 150,
      render: (_, r) => <span style={{ fontWeight: 700, color: T.primary, fontFamily: "monospace", fontSize: 12 }}>{orderDocNo(r)}</span>,
    },
    {
      title: <b>{t("product", "Product")}</b>, dataIndex: "product", key: "product",
      render: (v, r) => <span style={{ color: T.text }}>{parseLocalizedName(v, i18n.language, `#${r.product_id}`)}</span>,
    },
    {
      title: <b>{t("farmer", "Farmer")}</b>, key: "farmer", width: 140,
      render: (_, r) => {
        const name = farmerNameOf(r, i18n.language);
        return <span style={{ fontWeight: 600, color: name === "—" ? T.muted : T.text }}>{name}</span>;
      },
    },
    {
      title: <b>{t("qtyKg", "Qty / kg")}</b>, key: "qty", width: 140,
      render: (_, r) => {
        const q = parseFloat(r.quantity) || 0;
        if (!q) return "—";
        return r.is_weight
          ? <Tag color="green">{q} {t("kg", "KG")}</Tag>
          : <Tag color="blue">{q} {t("qty", "qty")}</Tag>;
      },
    },
    {
      title: <b>{t("posOrdersReturned", "Returned")}</b>, dataIndex: "refund_amount", key: "refund_amount", width: 140,
      render: (v) => <span style={{ color: T.red, fontWeight: 600 }}>−{formatCurrency(v, currency)}</span>,
    },
    {
      title: <b>{t("processedBy", "Processed by")}</b>, key: "processed_by", width: 140,
      render: (_, r) => <span style={{ color: T.sub }}>{r.processed_by_name || r.cashier || "—"}</span>,
    },
    {
      title: <b>{t("time", "Time")}</b>, dataIndex: "created_at", key: "created_at", width: 110,
      render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM HH:mm") : "—"}</span>,
    },
  ];

  const cancelColumns = [
    { title: "#", key: "idx", width: 50, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    {
      title: <b>{t("order", "Order")}</b>, key: "document_no", width: 150,
      render: (_, r) => <span style={{ fontWeight: 700, color: T.primary, fontFamily: "monospace", fontSize: 12 }}>{orderDocNo(r)}</span>,
    },
    {
      title: <b>{t("cashier", "Cashier")}</b>, dataIndex: "cashier", key: "cashier", width: 130,
      render: (v) => <span style={{ color: T.sub }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("customer", "Customer")}</b>, dataIndex: "customer", key: "customer", width: 140,
      render: (v) => <span style={{ color: T.muted }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("farmers", "Farmers")}</b>, key: "farmers", width: 160,
      render: (_, r) => {
        const names = [...new Set(
          (r.items || [])
            .map((it) => farmerNameOf(it, i18n.language))
            .filter((n) => n && n !== "—")
        )];
        if (!names.length) return <span style={{ color: T.muted }}>—</span>;
        return (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {names.map((n) => (
              <Tag key={n} color="geekblue" style={{ margin: 0, fontWeight: 600 }}>{n}</Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: <b>{t("method", "Method")}</b>, dataIndex: "payment_method", key: "payment_method", width: 100,
      render: (v) => (
        <Tag color={v === "cash" ? "green" : "orange"}>
          {v === "cash" ? t("cash", "Cash") : t("credit", "Credit")}
        </Tag>
      ),
    },
    {
      title: <b>{t("registerCancelled", "Cancelled")}</b>, dataIndex: "total_price", key: "total_price", width: 130,
      render: (v, r) => (
        <span style={{ color: T.red, fontWeight: 700 }}>
          −{formatCurrency(parseFloat(r.refunded_amount) > 0 ? r.refunded_amount : v, currency)}
        </span>
      ),
    },
    {
      title: <b>{t("reason", "Reason")}</b>, dataIndex: "cancellation_reason", key: "cancellation_reason",
      render: (v) => <span style={{ color: T.sub, fontSize: 12 }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("time", "Time")}</b>, key: "time", width: 110,
      render: (_, r) => {
        const when = r.cancelled_at || r.created_at;
        return <span style={{ color: T.muted, fontSize: 12 }}>{when ? dayjs(when).format("DD/MM HH:mm") : "—"}</span>;
      },
    },
  ];

  const orderColumns = [
    { title: "#", key: "idx", width: 50, render: (_, __, i) => <span style={{ color: T.muted }}>{i + 1}</span> },
    {
      title: <b>{t("order", "Order")}</b>, key: "document_no", width: 150,
      render: (_, r) => <span style={{ fontWeight: 700, color: T.primary, fontFamily: "monospace", fontSize: 12 }}>{orderDocNo(r)}</span>,
    },
    {
      title: <b>{t("cashier", "Cashier")}</b>, dataIndex: "cashier", key: "cashier",
      render: (v) => <span style={{ color: T.sub }}>{v}</span>,
    },
    {
      title: <b>{t("customer", "Customer")}</b>, dataIndex: "customer", key: "customer",
      render: (v) => <span style={{ color: T.muted }}>{v || "—"}</span>,
    },
    {
      title: <b>{t("farmers", "Farmers")}</b>, key: "farmers", width: 180,
      render: (_, r) => {
        const names = [...new Set(
          (r.items || [])
            .map((it) => farmerNameOf(it, i18n.language))
            .filter((n) => n && n !== "—")
        )];
        if (!names.length) return <span style={{ color: T.muted }}>—</span>;
        return (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            {names.map((n) => (
              <Tag key={n} color="geekblue" style={{ margin: 0, fontWeight: 600 }}>{n}</Tag>
            ))}
          </span>
        );
      },
    },
    {
      title: <b>{t("method", "Method")}</b>, dataIndex: "payment_method", key: "payment_method",
      render: (v, r) => {
        const returnedAmt = Math.max(parseFloat(r.total_returned) || 0, parseFloat(r.refunded_amount) || 0);
        const isReturned = r.status === "returned" || (r.status !== "cancelled" && returnedAmt > 0);
        return (
          <span style={{ display: "inline-flex", gap: 4, flexWrap: "wrap" }}>
            <Tag color={v === "cash" ? "green" : "orange"}>{v === "cash" ? t("cash", "Cash") : t("credit", "Credit")}</Tag>
            {r.status === "cancelled" && <Tag color="red">{t("statusCancelled", "Cancelled")}</Tag>}
            {r.status === "returned" && <Tag color="orange">{t("statusReturned", "Returned")}</Tag>}
            {r.status !== "returned" && r.status !== "cancelled" && returnedAmt > 0 && (
              <Tag color="orange">{t("statusPartialReturn", "Partial return")}</Tag>
            )}
            {isReturned && returnedAmt > 0 && (
              <span style={{ fontSize: 11, color: T.red, fontWeight: 600 }}>−{formatCurrency(returnedAmt, currency)}</span>
            )}
          </span>
        );
      },
    },
    {
      title: <b>{t("total", "Total")}</b>, dataIndex: "total_price", key: "total_price",
      render: (v, r) => (
        <div>
          <b style={{ color: r.status === "cancelled" ? T.red : T.text, textDecoration: r.status === "cancelled" ? "line-through" : undefined }}>
            {formatCurrency(v, currency)}
          </b>
          {r.status === "cancelled" && r.cancellation_reason && (
            <div style={{ fontSize: 11, color: T.muted }}>{r.cancellation_reason}</div>
          )}
          {!(r.is_taxed === false || r.is_taxed === 0) && Number(r.commission_amount) > 0 && r.status !== "cancelled" && (
            <div style={{ fontSize: 11, color: "#d97706", fontWeight: 600 }}>
              +{formatCurrency(r.commission_amount, currency)} {t("posTerminalCommission", "comm.")}
            </div>
          )}
        </div>
      ),
    },
    {
      title: <b>{t("time", "Time")}</b>, dataIndex: "created_at", key: "created_at",
      render: (v) => <span style={{ color: T.muted, fontSize: 12 }}>{v ? dayjs(v).format("DD/MM HH:mm") : "—"}</span>,
    },
  ];

  const rangeLabel = dateRange?.[0] && dateRange?.[1]
    ? `${dateRange[0].format("DD/MM/YYYY HH:mm")} → ${dateRange[1].format("DD/MM/YYYY HH:mm")}`
    : "—";

  const filterHint = [
    selectedUser?.user_name,
    farmerOptions.find((f) => String(f.value) === String(selectedFarmerId))?.label,
  ].filter(Boolean).join(" · ");

  return (
    <div className="daily-register-page velan-page" style={{ padding: "clamp(12px, 2.5vw, 24px)", background: T.bg, minHeight: "100%" }}>
      <PageHeader
        breadcrumb={[t("posLabel", "POS"), t("dailyRegisterTitle", "Daily Register")]}
        title={t("dailyRegisterTitle", "Daily Register")}
        subtitle={t("dailyRegisterSubtitle", "Sales summary — filter by date, cashier, or farmer")}
      />

      <div className="daily-register-panel" style={{ ...cardStyle, marginBottom: 14, padding: "12px 16px" }}>
        <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>{shiftHint}</div>
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <DateRangeFilter
            value={dateRange}
            onChange={(v) => {
              // Only commit when both ends are set — avoid snapping back to "today"
              // while the user is still picking the second date/time.
              if (v?.[0] && v?.[1]) setDateRange(v);
            }}
            showTime
            allowClear={false}
            size="small"
          />
          <Button
            size="small"
            style={{ borderRadius: 7 }}
            onClick={() => setDateRange(defaultRegisterRange(sessionUser, { shift: true }))}
          >
            {t("registerMyShift", "My shift")}
          </Button>
          <Button
            size="small"
            style={{ borderRadius: 7 }}
            onClick={() => setDateRange(defaultRegisterRange())}
          >
            {t("dashboardPresetToday", "Today")}
          </Button>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 200 }}
            placeholder={t("allCashiers", "All cashiers")}
            value={selectedUser?.user_id ?? undefined}
            onChange={(uid) => { setSelectedUser(uid == null ? null : (data?.by_user || []).find((u) => u.user_id === uid) || null); resetAllPages(); }}
            options={(data?.by_user || []).map((u) => ({ value: u.user_id, label: u.user_name }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ minWidth: 200 }}
            placeholder={t("allFarmers", "All farmers")}
            value={selectedFarmerId ?? undefined}
            onChange={(id) => { setSelectedFarmerId(id ?? null); resetAllPages(); }}
            options={farmerOptions}
          />
        </div>
      </div>

      <Spin spinning={loading}>
        {data && (
          <div className="daily-register-stack">
            <div className="daily-register-summary velan-stats-row velan-stats-row--dense">
              <StatCard
                icon={<ShoppingCartOutlined style={{ color: "#2563eb" }} />}
                label={t("registerSold", "Sold")}
                value={formatCurrency(summary.sold, currency)}
                iconBg="#dbeafe"
                valueColor={T.text}
              />
              <StatCard
                icon={<RollbackOutlined style={{ color: "#ea580c" }} />}
                label={t("registerReturned", "Returned")}
                value={formatCurrency(summary.returned, currency)}
                iconBg="#ffedd5"
                valueColor={summary.returned > 0 ? T.red : T.muted}
              />
              <StatCard
                icon={<StopOutlined style={{ color: "#be123c" }} />}
                label={t("registerCancelled", "Cancelled")}
                value={formatCurrency(summary.cancelled, currency)}
                iconBg="#ffe4e6"
                valueColor={summary.cancelled > 0 ? T.red : T.muted}
              />
              <StatCard
                icon={<DollarOutlined style={{ color: "#16a34a" }} />}
                label={t("registerPaid", "Paid")}
                value={formatCurrency(summary.paid, currency)}
                iconBg="#dcfce7"
                valueColor={T.green}
              />
              <StatCard
                icon={<CreditCardOutlined style={{ color: "#e11d48" }} />}
                label={t("registerDebt", "Debt")}
                value={formatCurrency(summary.debt, currency)}
                iconBg="#fee2e2"
                valueColor={summary.debt > 0 ? T.red : T.muted}
              />
            </div>

            <div className="daily-register-panel" style={cardStyle}>
              <div className="daily-register-panel-head">
                <span className="daily-register-panel-title">{t("byCashier", "By Cashier")}</span>
                <span className="daily-register-panel-hint">{t("clickNameToFilter", "click a name to filter")}</span>
              </div>
              <Table
                size="small"
                dataSource={(data.by_user || []).map((u, i) => ({ ...u, key: u.user_id ?? i }))}
                columns={userColumns}
                pagination={false}
                scroll={{ x: 500 }}
                rowClassName={(r) => selectedUser?.user_id === r.user_id ? "ant-table-row-selected" : ""}
                onRow={(record) => ({
                  onClick: () => { setSelectedUser(selectedUser?.user_id === record.user_id ? null : record); resetAllPages(); },
                  style: { cursor: "pointer", background: selectedUser?.user_id === record.user_id ? "#eff6ff" : undefined },
                })}
              />
            </div>

            <div className="daily-register-panel" style={cardStyle}>
              <div className="daily-register-panel-head" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="daily-register-panel-title">
                  {selectedUser ? t("cashierTransactions", "{{name}}'s Transactions", { name: selectedUser.user_name }) : t("allTransactions", "All Transactions")}
                </span>
                <Tag color="blue">{filteredOrders.length}</Tag>
                {(selectedUser || selectedFarmerId != null) && (
                  <Button
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => { setSelectedUser(null); setSelectedFarmerId(null); resetAllPages(); }}
                    style={{ marginInlineStart: "auto" }}
                  >
                    {t("showAll", "Show All")}
                  </Button>
                )}
              </div>
              <Table
                size="small"
                dataSource={filteredOrders.map((o, i) => ({ ...o, key: o.id ?? i }))}
                columns={orderColumns}
                pagination={{ ...ordersPagination, size: "small" }}
                scroll={{ x: 780 }}
                onRow={(record) => ({
                  style: record.status === "cancelled" ? { opacity: 0.65 } : undefined,
                })}
                expandable={{
                  expandedRowRender: (record) => (
                    <div className="daily-register-nested-table">
                      <Table
                        size="small"
                        dataSource={(record.items || [])
                          .filter((it) => selectedFarmerId == null || String(it.vendor_id) === String(selectedFarmerId))
                          .map((it, i) => ({ ...it, key: `${record.id}-${it.product_id ?? i}` }))}
                        columns={orderItemColumns}
                        pagination={false}
                        locale={{ emptyText: t("noDataFound", "No data found") }}
                      />
                    </div>
                  ),
                  rowExpandable: (record) => (record.items || []).length > 0,
                }}
              />
            </div>

            <div className="daily-register-panel" style={cardStyle}>
              <div className="daily-register-panel-head" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="daily-register-panel-title">{t("returns", "Returns")}</span>
                <Tag color="orange">{dayReturns.length}</Tag>
                {filterHint && (
                  <span className="daily-register-panel-hint" style={{ marginInlineStart: "auto" }}>
                    {filterHint}
                  </span>
                )}
              </div>
              <Table
                size="small"
                dataSource={dayReturns.map((r, i) => ({ ...r, key: r.id ?? `ret-${r.order_id}-${r.product_id}-${i}` }))}
                columns={returnColumns}
                pagination={{ ...returnsPagination, size: "small", hideOnSinglePage: true }}
                scroll={{ x: 700 }}
                locale={{ emptyText: t("noReturnsOnDate", "No returns in this range") }}
              />
            </div>

            <div className="daily-register-panel" style={cardStyle}>
              <div className="daily-register-panel-head" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="daily-register-panel-title">{t("posCancellationsTitle", "Cancellations")}</span>
                <Tag color="red">{dayCancellations.length}</Tag>
                {filterHint && (
                  <span className="daily-register-panel-hint" style={{ marginInlineStart: "auto" }}>
                    {filterHint}
                  </span>
                )}
              </div>
              <Table
                size="small"
                dataSource={dayCancellations.map((o, i) => ({ ...o, key: o.id ?? `cancel-${i}` }))}
                columns={cancelColumns}
                pagination={{ ...cancellationsPagination, size: "small", hideOnSinglePage: true }}
                scroll={{ x: 900 }}
                expandable={{
                  expandedRowRender: (record) => (
                    <div className="daily-register-nested-table">
                      <Table
                        size="small"
                        dataSource={(record.items || [])
                          .filter((it) => selectedFarmerId == null || String(it.vendor_id) === String(selectedFarmerId))
                          .map((it, i) => ({ ...it, key: `c-${record.id}-${it.product_id ?? i}` }))}
                        columns={orderItemColumns}
                        pagination={false}
                        locale={{ emptyText: t("noDataFound", "No data found") }}
                      />
                    </div>
                  ),
                  rowExpandable: (record) => (record.items || []).length > 0,
                }}
                locale={{ emptyText: t("noCancellationsOnDate", "No cancellations in this range") }}
              />
            </div>

            <div className="daily-register-panel" style={cardStyle}>
              <div className="daily-register-panel-head" style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span className="daily-register-panel-title">{t("productsSold", "Products Sold")}</span>
                <Tag color="green">{productsSold.length}</Tag>
                {filterHint && (
                  <span className="daily-register-panel-hint" style={{ marginInlineStart: "auto" }}>
                    {filterHint}
                  </span>
                )}
              </div>
              <Table
                size="small"
                dataSource={productsSold}
                columns={productsSoldColumns}
                pagination={{ ...productsSoldPagination, size: "small", hideOnSinglePage: true }}
                scroll={{ x: 600 }}
                locale={{ emptyText: t("noProductsSold", "No products sold") }}
              />
            </div>
          </div>
        )}
        {!loading && data && filteredOrders.length === 0 && dayReturns.length === 0 && dayCancellations.length === 0 && (
          <div style={{ ...cardStyle, textAlign: "center", padding: 60, color: T.muted }}>
            {t("noSalesOnDate", "No sales on {{date}}", { date: rangeLabel })}
          </div>
        )}
      </Spin>
    </div>
  );
};

export default DailyRegister;
